import { CREDENTIAL_TYPE_META, CREDENTIAL_TYPE_ORDER, CREDENTIAL_SCOPE_TYPES, CREDENTIAL_SCOPE_LABELS } from "@/lib/credentials";

const labelClass = "text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500";
const inputClass = "mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-900 dark:border-white/15 dark:bg-white/5 dark:text-white";

const scopeReferenceOptions = (scopeType, meta) => {
  if (scopeType === "team") return (meta?.teams || []).map((t) => ({ value: t.team_id, label: t.name }));
  if (scopeType === "location") return (meta?.locations || []).map((l) => ({ value: l.location_id, label: l.name }));
  if (scopeType === "service_area") return (meta?.service_areas || []).map((s) => ({ value: s.service_area_id, label: s.name }));
  return null;
};

// Shared field set for both the Add and Edit credential forms (Phase 1: core fields only).
export const CredentialFormFields = ({ form, setForm, meta, testIdPrefix }) => {
  const patch = (fields) => setForm((f) => ({ ...f, ...fields }));
  const scopeOptions = scopeReferenceOptions(form.scope_type, meta);
  const canManageOrg = !!meta?.can_manage_organization_credentials;

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={labelClass}>Credential Type *</label>
          <select value={form.credential_type} onChange={(e) => patch({ credential_type: e.target.value })} data-testid={`${testIdPrefix}-type`} className={inputClass}>
            {CREDENTIAL_TYPE_ORDER.map((t) => <option key={t} value={t}>{CREDENTIAL_TYPE_META[t].singular}</option>)}
          </select>
        </div>
        <div>
          <label className={labelClass}>Credential Name *</label>
          <input type="text" value={form.credential_name} onChange={(e) => patch({ credential_name: e.target.value })} placeholder="e.g. Electrical Contractor License" data-testid={`${testIdPrefix}-name`} className={inputClass} />
        </div>
      </div>

      <div>
        <label className={labelClass}>Description</label>
        <textarea value={form.description} onChange={(e) => patch({ description: e.target.value })} rows={2} placeholder="Optional short description" data-testid={`${testIdPrefix}-description`} className={inputClass} />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={labelClass}>Credential / License / Policy Number</label>
          <input type="text" value={form.credential_number} onChange={(e) => patch({ credential_number: e.target.value })} placeholder="Optional" data-testid={`${testIdPrefix}-number`} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Issued By</label>
          <input type="text" value={form.issuer} onChange={(e) => patch({ issuer: e.target.value })} placeholder="e.g. State of Florida" data-testid={`${testIdPrefix}-issuer`} className={inputClass} />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={labelClass}>Holder *</label>
          <select
            value={form.holder_type}
            onChange={(e) => patch({ holder_type: e.target.value, holder_id: e.target.value === "organization" ? null : form.holder_id })}
            disabled={!canManageOrg}
            data-testid={`${testIdPrefix}-holder-type`}
            className={`${inputClass} disabled:opacity-60`}
          >
            {canManageOrg && <option value="organization">Organization</option>}
            <option value="person">Person</option>
          </select>
        </div>
        {form.holder_type === "person" && (
          <div>
            <label className={labelClass}>Person *</label>
            <select value={form.holder_id || ""} onChange={(e) => patch({ holder_id: e.target.value || null })} data-testid={`${testIdPrefix}-holder-person`} className={inputClass}>
              <option value="">Select a person…</option>
              {(meta?.users || []).map((u) => <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>)}
            </select>
          </div>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={labelClass}>Issue Date</label>
          <input type="date" value={form.issue_date || ""} onChange={(e) => patch({ issue_date: e.target.value || null })} data-testid={`${testIdPrefix}-issue-date`} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Expiration Date</label>
          <input
            type="date"
            value={form.expiration_date || ""}
            onChange={(e) => patch({ expiration_date: e.target.value || null })}
            disabled={form.does_not_expire}
            data-testid={`${testIdPrefix}-expiration-date`}
            className={`${inputClass} disabled:opacity-50`}
          />
          <label className="mt-2 flex items-center gap-2 text-xs font-semibold text-slate-500 dark:text-slate-400">
            <input
              type="checkbox"
              checked={form.does_not_expire}
              onChange={(e) => patch({ does_not_expire: e.target.checked, expiration_date: e.target.checked ? null : form.expiration_date })}
              data-testid={`${testIdPrefix}-does-not-expire`}
            />
            Does not expire
          </label>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={labelClass}>Operational Scope</label>
          <select
            value={form.scope_type || ""}
            onChange={(e) => patch({ scope_type: e.target.value || null, scope_reference: null })}
            data-testid={`${testIdPrefix}-scope-type`}
            className={inputClass}
          >
            <option value="">None</option>
            {CREDENTIAL_SCOPE_TYPES.map((s) => <option key={s} value={s}>{CREDENTIAL_SCOPE_LABELS[s]}</option>)}
          </select>
        </div>
        {form.scope_type && form.scope_type !== "organization_wide" && form.scope_type !== "individual" && (
          <div>
            <label className={labelClass}>{form.scope_type === "jurisdiction" ? "Jurisdiction" : "Select"}</label>
            {form.scope_type === "jurisdiction" ? (
              <input type="text" value={form.scope_reference || ""} onChange={(e) => patch({ scope_reference: e.target.value })} placeholder="e.g. Florida" data-testid={`${testIdPrefix}-scope-reference`} className={inputClass} />
            ) : (
              <select value={form.scope_reference || ""} onChange={(e) => patch({ scope_reference: e.target.value || null })} data-testid={`${testIdPrefix}-scope-reference`} className={inputClass}>
                <option value="">Select…</option>
                {(scopeOptions || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            )}
          </div>
        )}
      </div>

      <div>
        <label className={labelClass}>Notes</label>
        <textarea value={form.notes} onChange={(e) => patch({ notes: e.target.value })} rows={3} placeholder="Optional long-form notes" data-testid={`${testIdPrefix}-notes`} className={inputClass} />
      </div>
    </div>
  );
};
